import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, MagnifyingGlassPlus } from "@phosphor-icons/react";

/**
 * ImageCropper — reusable modal for cropping uploaded images.
 *
 * Props:
 *   src         data URL of the image to crop
 *   aspect      crop aspect ratio (default 1)
 *   cropShape   "round" | "rect" (default "round")
 *   outputSize  { width, height } final exported size in px (default 512x512)
 *   onConfirm(blob)  called with cropped PNG blob
 *   onCancel()
 */
export default function ImageCropper({
    src,
    aspect = 1,
    cropShape = "round",
    outputSize = { width: 512, height: 512 },
    onConfirm,
    onCancel,
}) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [busy, setBusy] = useState(false);

    const onCropComplete = useCallback((_area, areaPixels) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const confirm = async () => {
        if (!croppedAreaPixels) return;
        setBusy(true);
        try {
            const blob = await getCroppedBlob(src, croppedAreaPixels, outputSize);
            onConfirm(blob);
        } finally {
            setBusy(false);
        }
    };

    // Pick a sensible visual stage size based on aspect
    const stageStyle = aspect >= 1
        ? { aspectRatio: `${aspect} / 1`, width: "100%" }
        : { aspectRatio: `${aspect} / 1`, width: "70%", margin: "0 auto" };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={onCancel}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-wta-surface border border-wta-border rounded-sm overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-wta-border">
                    <div className="font-heading text-xl tracking-wider">Crop Photo</div>
                    <button data-testid="close-cropper" onClick={onCancel} className="text-wta-muted hover:text-white"><X size={20} /></button>
                </div>
                <div className="relative w-full bg-black" style={stageStyle}>
                    <Cropper
                        image={src}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        cropShape={cropShape}
                        showGrid={cropShape === "rect"}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>
                <div className="p-4 border-t border-wta-border">
                    <label className="text-xs tracking-widest text-wta-muted uppercase flex items-center gap-2">
                        <MagnifyingGlassPlus size={14} /> Zoom
                    </label>
                    <input
                        data-testid="cropper-zoom"
                        type="range" min="1" max="4" step="0.05"
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="mt-2 w-full accent-wta-primary"
                    />
                    <div className="text-xs text-wta-muted mt-1">Drag to pan · pinch or scroll to zoom</div>
                    <div className="mt-4 flex gap-3">
                        <button data-testid="cropper-cancel" onClick={onCancel} className="btn-ghost flex-1 px-4 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">Cancel</button>
                        <button data-testid="cropper-confirm" disabled={busy || !croppedAreaPixels} onClick={confirm} className="btn-primary flex-1 px-4 py-2 rounded-sm text-sm disabled:opacity-50">
                            {busy ? "Cropping…" : "Use Photo"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

async function getCroppedBlob(src, area, size) {
    const img = await loadImage(src);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
        img,
        area.x, area.y, area.width, area.height,
        0, 0, size.width, size.height,
    );
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.92));
}
